import { useCallback, useEffect, type FC } from 'react';

import useElementEvents from '@core/hooks/cohesion/useElementEvents';
import type { VoyagerResult } from '@core/ts/results';
import { isUnderGraduate } from '@core/utils/isUnderGraduate';

import { type EventingOverrides, productViewed } from '../../services/events/productEvents';
import type { UserPreferencesAndMilitary } from '../../types';
import RecommenderProgramSelect from './RecommenderProgramSelect';
import SpotlightProgramSelect from './SpotlightProgramSelect';

type Props = {
  spotlightMatch: VoyagerResult;
  onFlowExit: () => void;
  firstName?: string;
  onProgramCtaClick: (result: VoyagerResult, eventingOverrides: EventingOverrides) => void;
  recommenderMatches: VoyagerResult[];
  publisherName: string;
  userPreferencesAndMilitary: UserPreferencesAndMilitary;
  degree: string;
  subject: string;
};

const ProgramSelect: FC<Props> = ({
  spotlightMatch,
  recommenderMatches,
  userPreferencesAndMilitary,
  degree,
  subject,
  onProgramCtaClick,
  onFlowExit,
  ...otherProps
}) => {
  const { elementClicked } = useElementEvents();

  const spotlightProductEventing: EventingOverrides = {
    location: 'voyager-spotlight-pop-up',
    formatSubtype: 'recommendation',
    customDimensions: [{ key: 'isUserRequested', value: 'true' }],
    position: 1,
  };
  const recommenderPopupProductEventing: EventingOverrides = {
    location: 'voyager-recommender-pop-up',
    formatSubtype: 'recommendation',
    formatType: 'multi-program',
    customDimensions: [{ key: 'isUserRequested', value: 'true' }],
  };

  const productEventing = spotlightMatch ? spotlightProductEventing : recommenderPopupProductEventing;

  const ProgramSelectComponent = spotlightMatch ? SpotlightProgramSelect : RecommenderProgramSelect;

  const handleCardView = useCallback(
    (result: VoyagerResult) => {
      productViewed(result, userPreferencesAndMilitary, productEventing);
    },
    [productEventing, userPreferencesAndMilitary]
  );

  const handleFlowExit = () => {
    elementClicked({
      webElement: {
        elementType: 'exit-element',
        text: 'Back to All Results',
        htmlId: 'exit-back',
        name: 'exit-application-step',
        location: productEventing.location,
      },
    });
    onFlowExit?.();
  };

  const programClick = (result: VoyagerResult) => onProgramCtaClick(result, productEventing);

  return (
    <ProgramSelectComponent
      onProgramCtaClick={programClick}
      spotlightMatch={spotlightMatch}
      onView={handleCardView}
      recommenderMatches={recommenderMatches}
      onFlowExit={handleFlowExit}
      userPreferencesAndMilitary={userPreferencesAndMilitary}
      degree={degree}
      subject={subject}
      eventingOverrides={productEventing}
      {...otherProps}
    />
  );
};

export default ProgramSelect;
